import { useMemo, useRef, useState } from 'react';
import { Badge } from '@savvy-kit/components/badge';
import { Button } from '@savvy-kit/components/button';
import { InputGroup, InputGroupAddon, InputGroupInput } from '@savvy-kit/components/input-group';
import { Popover, PopoverContent } from '@savvy-kit/components/popover';
import { Text } from '@savvy-kit/foundations/text';
import { cn } from '@savvy-kit/lib/utils';
import { useReactFlow } from '@xyflow/react';
import { SearchIcon } from 'lucide-react';

import type { ContactWebGraphNode } from './types';

const MAX_RESULTS = 8;
const FALLBACK_NODE_WIDTH = 224;
const FALLBACK_NODE_HEIGHT = 88;
const LISTBOX_ID = 'contact-web-search-listbox';

type Props = {
  readonly nodes: readonly ContactWebGraphNode[];
  readonly onSelectNode: (nodeId: string) => void;
};

export function ContactWebSearch({ nodes, onSelectNode }: Props) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const { getNode, setCenter, getZoom } = useReactFlow();

  const results = useMemo(() => {
    const trimmed = query.trim().toLowerCase();
    if (trimmed === '') return [];
    return nodes.filter((node) => node.name.toLowerCase().includes(trimmed)).slice(0, MAX_RESULTS);
  }, [nodes, query]);

  const showResults = open && results.length > 0;

  const handleSelect = (nodeId: string) => {
    onSelectNode(nodeId);
    setQuery('');
    setOpen(false);

    const node = getNode(nodeId);
    if (node) {
      const width = node.measured?.width ?? FALLBACK_NODE_WIDTH;
      const height = node.measured?.height ?? FALLBACK_NODE_HEIGHT;
      void setCenter(node.position.x + width / 2, node.position.y + height / 2, { zoom: getZoom(), duration: 400 });
    }
  };

  return (
    <Popover
      open={showResults}
      onOpenChange={(nextOpen, details) => {
        if (
          !nextOpen &&
          details.reason === 'outside-press' &&
          details.event.target instanceof Node &&
          inputRef.current?.contains(details.event.target) === true
        ) {
          details.cancel();
          return;
        }
        setOpen(nextOpen);
      }}
    >
      <InputGroup className="w-64 bg-white">
        <InputGroupAddon>
          <SearchIcon />
        </InputGroupAddon>
        <InputGroupInput
          ref={inputRef}
          value={query}
          placeholder="Find a client or contact"
          role="combobox"
          aria-expanded={showResults}
          aria-autocomplete="list"
          aria-controls={LISTBOX_ID}
          onChange={(event) => {
            setQuery(event.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={(event) => {
            if (event.key === 'Escape') {
              setQuery('');
              setOpen(false);
              inputRef.current?.blur();
            }
          }}
        />
      </InputGroup>
      <PopoverContent
        anchor={inputRef}
        align="start"
        sideOffset={4}
        className="p-1"
        style={{ width: 'var(--anchor-width)' }}
        initialFocus={false}
        finalFocus={false}
      >
        <ul className="flex max-h-72 flex-col gap-0.5 overflow-y-auto">
          {results.map((node) => (
            <li key={node.id}>
              <Button
                variant="ghost"
                onClick={() => handleSelect(node.id)}
                className="h-auto w-full justify-start gap-2 px-2 py-1.5 font-normal"
              >
                <Badge
                  type="dot"
                  className={cn(
                    'size-2.5 border-transparent',
                    node.kind === 'household' ? 'bg-primary' : 'bg-savvy-gold',
                  )}
                />
                <Text variant="body-sm" color="dark" className="min-w-0 flex-1 truncate">
                  {node.name}
                </Text>
                <Text variant="caption" color="secondary" className="shrink-0">
                  {node.kind === 'household' ? 'Household' : 'Contact'}
                </Text>
              </Button>
            </li>
          ))}
        </ul>
      </PopoverContent>
    </Popover>
  );
}
